import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FaArrowLeft, FaPlane, FaGraduationCap, FaExternalLinkAlt, 
  FaCalendarAlt, FaMapMarkerAlt, FaHashtag, FaTimes, 
  FaChevronLeft, FaChevronRight, FaShareAlt, FaCheck, FaVrCardboard,
  FaUniversity, FaRocket, FaCamera, FaExpandAlt, FaUsers,
  FaHandshake, FaUserTie, FaChalkboardTeacher, FaPassport,
  FaGlobe, FaHeart, FaBookOpen, FaStar, FaArrowRight,
  FaLinkedinIn, FaSearch
} from 'react-icons/fa';
import { indiaTripOverview, indiaPosts } from '../data/indiaData';
import './IndiaVisit.css';

const STAT_ICONS = {
  graduation: <FaGraduationCap />,
  calendar: <FaCalendarAlt />,
  university: <FaUniversity />,
  plane: <FaPlane />,
  vr: <FaVrCardboard />,
};

export default function IndiaVisit({ onBackToHome }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxState, setLightboxState] = useState({ isOpen: false, postIndex: 0, imageIndex: 0 });
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [heroImgLoaded, setHeroImgLoaded] = useState(false);

  // Stable callbacks to avoid re-creating on every render
  const closeLightbox = useCallback(() => {
    setLightboxState(prev => ({ ...prev, isOpen: false }));
    document.body.style.overflow = 'auto';
  }, []);

  const prevImage = useCallback(() => {
    setLightboxState(prev => {
      const currentPost = indiaPosts.find((_, idx) => idx === prev.postIndex) || indiaPosts[prev.postIndex];
      if (!currentPost) return prev;
      const total = currentPost.images.length;
      return { ...prev, imageIndex: (prev.imageIndex - 1 + total) % total };
    });
  }, []);

  const nextImage = useCallback(() => {
    setLightboxState(prev => {
      const currentPost = indiaPosts[prev.postIndex];
      if (!currentPost) return prev;
      const total = currentPost.images.length;
      return { ...prev, imageIndex: (prev.imageIndex + 1) % total };
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxState.isOpen) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxState.isOpen, closeLightbox, prevImage, nextImage]);

  // Preload the first image of each post on mount
  useEffect(() => {
    indiaPosts.forEach(post => {
      if (post.images.length > 0) {
        const img = new Image();
        img.src = `${encodeURI(post.imageFolder)}/${post.images[0].filename}`;
      }
    });
    setHeroImgLoaded(true);
  }, []);

  const openLightbox = useCallback((postIdx, imgIdx) => {
    setLightboxState({ isOpen: true, postIndex: postIdx, imageIndex: imgIdx });
    document.body.style.overflow = 'hidden';
  }, []);

  const copyShareLink = useCallback((postId) => {
    const shareUrl = `${window.location.origin}${window.location.pathname}#india-post-${postId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedId(postId);
    setTimeout(() => setCopiedId(null), 2500);
  }, []);

  // Memoized filtered posts
  const filteredPosts = useMemo(() => {
    return indiaPosts.filter(post => {
      const matchesFilter = activeFilter === 'all' || post.id === activeFilter;
      const query = searchQuery.toLowerCase().trim();
      if (!query) return matchesFilter;
      const matchesSearch =
        post.title.toLowerCase().includes(query) ||
        post.subtitle.toLowerCase().includes(query) ||
        post.hashtags.some(h => h.toLowerCase().includes(query));
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  const activePost = lightboxState.isOpen ? filteredPosts[lightboxState.postIndex] : null;
  const activeImageObj = activePost ? activePost.images[lightboxState.imageIndex] : null;

  return (
    <div className="india-page">
      {/* ── Top Floating Bar ── */}
      <div className="india-top-bar">
        <div className="india-bar-inner">
          <button className="back-btn" onClick={onBackToHome}>
            <FaArrowLeft /> Back to Portfolio
          </button>
          <div className="india-bar-title">
            <FaGlobe className="bar-icon" />
            <span className="bar-highlight">Northern UNI</span>
            <span className="bar-sep">/</span>
            <span>India Mobility Programme 2026</span>
          </div>
          <div className="india-bar-actions">
            <FaPlane className="bar-flag-icon" />
          </div>
        </div>
      </div>

      {/* ── Hero Header ── */}
      <section className="india-hero">
        <div className="india-hero-bg"></div>
        <div className="india-hero-grid">
          <div className="india-hero-text">
            <div className="india-hero-badge">
              <span className="live-dot"></span>
              GLOBAL MOBILITY PROGRAMME 2026
            </div>

            <h1 className="india-hero-title">
              From Jaffna to India
              <em>7 Days of Innovation & Tech Exposure</em>
            </h1>

            <p className="india-hero-subtitle">
              Selected as one of the <strong>Top Performing 12 Students</strong> of Northern UNI for a 
              fully funded 1-week International Student Mobility Programme at 
              <strong> SIMATS Saveetha University, Chennai</strong>. Exploring AR/VR, IoT, robotics, and emerging technologies.
            </p>

            <div className="hero-cta-row">
              <a href="#india-post-schol-giving-day" className="btn-blk">
                <FaBookOpen /> Read the Journey
              </a>
              <a href="#india-post-day-05" className="btn-ghost">
                <FaRocket /> Latest: Day 05
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="india-stats-col">
            {indiaTripOverview.stats.map((stat, idx) => (
              <div key={idx} className="india-stat-card">
                <div className="stat-icon">{STAT_ICONS[stat.iconKey]}</div>
                <div className="stat-body">
                  <div className="stat-val">{stat.value}</div>
                  <div className="stat-lbl">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Acknowledgements & Mentors ── */}
      <section className="india-ack-sec">
        <div className="india-container">
          <div className="ack-header">
            <span className="sec-tag"><FaHandshake /> GRATITUDE & LEADERSHIP</span>
            <h2>Organizers, Mentors & Partners</h2>
            <p>Special recognition to the visionaries and supporters who made this landmark international journey possible for Northern UNI students.</p>
          </div>

          <div className="ack-grid">
            <div className="ack-item highlight">
              <div className="ack-icon"><FaStar /></div>
              <div className="ack-role">Sponsoring Organization</div>
              <h3>Synergy Consultant & Solutions (Pvt) Ltd</h3>
              <p>Fully funded visa, flight tickets, accommodation, food, and local travel arrangements for all 12 selected student delegates.</p>
            </div>

            <div className="ack-item">
              <div className="ack-icon"><FaUserTie /></div>
              <div className="ack-role">Director & Sponsor Lead</div>
              <h3>Mr. GAYAN WIJEYASEKARA</h3>
              <p>Director of SYNERGY CONSULTANT & Solutions (Pvt) Ltd — Presided over the visa and flight ticket presentation ceremony and encouraged us.</p>
            </div>

            <div className="ack-item">
              <div className="ack-icon"><FaChalkboardTeacher /></div>
              <div className="ack-role">Academic Lead & Faculty Escort</div>
              <h3>Siveneshvari Miss</h3>
              <p>Faculty member who travelled with us and guided us throughout the journey, providing invaluable mentorship and support.</p>
            </div>

            <div className="ack-item">
              <div className="ack-icon"><FaUsers /></div>
              <div className="ack-role">Programme Support Team</div>
              <h3>Sangeetha, Sulojan & Arthikan</h3>
              <p>Exceptional coordination, travel planning, and care throughout the visa process, departure, and India stay.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Filter & Search ── */}
      <section className="india-nav-sec">
        <div className="india-container">
          <div className="india-nav-wrapper">
            <div className="filter-tabs">
              <button 
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                All Chapters ({indiaPosts.length})
              </button>
              {indiaPosts.map(post => (
                <button
                  key={post.id}
                  className={`filter-btn ${activeFilter === post.id ? 'active' : ''}`}
                  onClick={() => setActiveFilter(post.id)}
                >
                  {post.badge}
                </button>
              ))}
            </div>

            <div className="search-box">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search posts, technologies, names..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search" onClick={() => setSearchQuery('')}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Posts ── */}
      <section className="india-posts-sec">
        <div className="india-container">
          {filteredPosts.length === 0 ? (
            <div className="no-posts-found">
              <FaSearch className="no-posts-icon" />
              <h3>No posts matched "{searchQuery}"</h3>
              <button className="btn-blk" onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}>
                Reset Filters
              </button>
            </div>
          ) : (
            filteredPosts.map((post, postIdx) => (
              <article key={post.id} id={`india-post-${post.id}`} className="india-post-card">
                {/* Hero image — first image as full-width cover */}
                <div className="post-cover-image">
                  <img 
                    src={`${encodeURI(post.imageFolder)}/${post.images[0].filename}`} 
                    alt={post.images[0].caption}
                    loading="lazy"
                    onClick={() => openLightbox(postIdx, 0)}
                  />
                  <div className="post-cover-overlay">
                    <span className="post-badge-floating">{post.badge}</span>
                  </div>
                </div>

                {/* Post Header */}
                <div className="post-body">
                  <div className="post-meta-strip">
                    <span className="post-meta-item"><FaCalendarAlt /> {post.dateLabel}</span>
                    <span className="post-meta-item"><FaMapMarkerAlt /> {post.location}</span>
                  </div>

                  <h2 className="post-title">{post.title}</h2>
                  <p className="post-subtitle">{post.subtitle}</p>

                  {/* Key Highlights */}
                  {post.highlights && (
                    <div className="post-key-takeaways">
                      <h4><FaStar /> Key Highlights & Milestones</h4>
                      <ul>
                        {post.highlights.map((h, i) => (
                          <li key={i}><FaCheck className="check-icon" /> {h}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Article Paragraphs */}
                  <div className="post-article-body">
                    {post.paragraphs.map((para, i) => (
                      <p key={i} className="post-paragraph">{para}</p>
                    ))}
                  </div>

                  {/* Tech List */}
                  {post.techList && (
                    <div className="post-tech-box">
                      <h4><FaVrCardboard /> Technologies & Tools Covered</h4>
                      <div className="post-tech-grid">
                        {post.techList.map((tech, i) => (
                          <span key={i} className="tech-chip">{tech}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gallery */}
                  <div className="post-gallery-sec">
                    <div className="gallery-header">
                      <h3><FaCamera /> Photo Journal — {post.images.length} Photos</h3>
                      <span className="gallery-hint"><FaExpandAlt /> Click to enlarge</span>
                    </div>

                    <div className="post-image-grid">
                      {post.images.map((img, imgIdx) => (
                        <div 
                          key={imgIdx} 
                          className="gallery-item"
                          onClick={() => openLightbox(postIdx, imgIdx)}
                        >
                          <img 
                            src={`${encodeURI(post.imageFolder)}/${img.filename}`} 
                            alt={img.caption || post.title} 
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="gallery-overlay">
                            <FaExpandAlt className="expand-icon" />
                            <span className="caption-preview">{img.caption}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hashtags & Share */}
                  <div className="post-footer">
                    <div className="post-hashtags">
                      {post.hashtags.slice(0, 6).map((tag, i) => (
                        <span key={i} className="hashtag-pill"><FaHashtag /> {tag}</span>
                      ))}
                    </div>

                    <button className="share-btn" onClick={() => copyShareLink(post.id)}>
                      {copiedId === post.id ? <><FaCheck /> Copied</> : <><FaShareAlt /> Share</>}
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}

          {/* Teaser */}
          <div className="india-teaser-card">
            <FaRocket className="teaser-icon" />
            <div className="teaser-badge">MORE CHAPTERS LOADING</div>
            <h2>Upcoming Chapters & Final Presentation</h2>
            <p>
              I am documenting our 7-day international journey step-by-step. 
              Check back soon for upcoming posts covering our industrial visits, cultural experiences, 
              and final project presentations in India.
            </p>
            <div className="teaser-pulse-bar">
              <span className="pulse-dot"></span> Journal updates in progress
            </div>
          </div>
        </div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxState.isOpen && activePost && activeImageObj && (
        <div className="lightbox-backdrop" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} title="Close (Esc)">
              <FaTimes />
            </button>

            <button className="lightbox-arrow left" onClick={prevImage} title="Previous (Left Arrow)">
              <FaChevronLeft />
            </button>

            <div className="lightbox-image-wrapper">
              <img 
                src={`${encodeURI(activePost.imageFolder)}/${activeImageObj.filename}`} 
                alt={activeImageObj.caption}
              />
              <div className="lightbox-caption-bar">
                <div className="caption-text">{activeImageObj.caption}</div>
                <div className="caption-meta">
                  <span>{activePost.badge}</span>
                  <span className="caption-sep">|</span>
                  <span>Photo {lightboxState.imageIndex + 1} of {activePost.images.length}</span>
                </div>
              </div>
            </div>

            <button className="lightbox-arrow right" onClick={nextImage} title="Next (Right Arrow)">
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* ── Bottom CTA ── */}
      <section className="india-bottom-sec">
        <div className="india-container text-center">
          <FaHeart className="bottom-heart-icon" />
          <h2>Thank you for reading my India Journey Journal</h2>
          <p>7 days. Countless memories. One unforgettable beginning. From Jaffna to India.</p>
          <div className="bottom-btn-group">
            <button className="btn-blk" onClick={onBackToHome}>
              <FaArrowLeft /> Return to Portfolio
            </button>
            <a 
              href="https://linkedin.com/in/marisilin-bijosilin" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-ghost"
            >
              <FaLinkedinIn /> Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
